/**
 * Luật thuần của màn Cấu hình vai trò và ngăn Gán vai trò (G2-D1-4, ADR-0004).
 *
 * role-service là chốt chặn thật. Ở đây chỉ có những gì giao diện cần để bắt
 * lỗi gõ nhầm sớm, gom danh mục quyền thành lưới, và đọc lỗi `pointer` của
 * server về đúng ô — đủ thuần để test bằng `node:test`.
 */
import type { Permission, Role } from '../types.ts';

/** Mẫu `code` theo hợp đồng: chữ hoa đầu, sau đó chữ hoa, số, gạch dưới; 2–63 ký tự. */
export const MAU_MA_VAI_TRO = /^[A-Z][A-Z0-9_]{1,62}$/;

/** Trần độ dài. Mã theo mẫu ở trên; tên đặt trần giao diện, server vẫn là chốt chặn. */
export const DAI_TOI_DA = { code: 63, name: 200 } as const;

export type LoiMa = 'trong' | 'dinhDang' | 'dai';

export function kiemMa(code: string): LoiMa | null {
  const c = code.trim();
  if (!c) return 'trong';
  if (c.length > DAI_TOI_DA.code) return 'dai';
  if (!MAU_MA_VAI_TRO.test(c)) return 'dinhDang';
  return null;
}

export type LoiTenVaiTro = 'trong' | 'dai';

export function kiemTenVaiTro(ten: string): LoiTenVaiTro | null {
  const t = ten.trim();
  if (!t) return 'trong';
  if (t.length > DAI_TOI_DA.name) return 'dai';
  return null;
}

/**
 * `module_code` của nhóm quyền do chính nền tảng cấp (`core:module:*`, ADR-0104). Không phải module nào nên đứng đầu
 * danh mục, và không gán được cho khoá API.
 */
export const MA_QUYEN_NEN_TANG = 'core';

/**
 * Mô tả quyền người dùng đọc, theo ngôn ngữ giao diện (ADR-0111). Thứ tự rơi: bản dịch của ngôn ngữ đang dùng, rồi
 * `description` (tiếng Việt), rồi mã quyền để dòng không trống. Ngôn ngữ thiếu bản dịch vẫn hiện mô tả tiếng Việt chứ
 * không hiện mã thô.
 */
export function moTaQuyen(q: Permission, ngonNgu: string): string {
  return q.description_i18n?.[ngonNgu]?.trim() || q.description?.trim() || q.code;
}

/** Phần `module` của mã quyền `{module}:{resource}:{action}`. */
export const moduleCuaQuyen = (code: string): string => code.split(':')[0] ?? code;

export interface NhomQuyen {
  module_code: string;
  module_name: string;
  quyen: Permission[];
}

/**
 * Gom danh mục quyền theo module, giữ thứ tự server trả trong từng module.
 * Nhóm quyền nền tảng đứng đầu; các module sau đó sắp theo tên hiển thị để
 * người dùng dò được bằng mắt.
 */
export function gomTheoModule(danhMuc: readonly Permission[], locale = 'vi'): NhomQuyen[] {
  const theoMa = new Map<string, NhomQuyen>();
  for (const q of danhMuc) {
    const nhom = theoMa.get(q.module_code);
    if (nhom) nhom.quyen.push(q);
    else theoMa.set(q.module_code, { module_code: q.module_code, module_name: q.module_name, quyen: [q] });
  }
  return [...theoMa.values()].sort(
    (a, b) =>
      Number(b.module_code === MA_QUYEN_NEN_TANG) - Number(a.module_code === MA_QUYEN_NEN_TANG) ||
      a.module_name.localeCompare(b.module_name, locale),
  );
}

export function batTat(daChon: ReadonlySet<string>, ma: string): Set<string> {
  const moi = new Set(daChon);
  if (moi.has(ma)) moi.delete(ma);
  else moi.add(ma);
  return moi;
}

/**
 * Quyền vai trò đang có mà danh mục không còn — module đã gỡ khỏi tổ chức.
 * Gửi lại chúng là `422`, nên ngăn phân quyền nói ra và bỏ chúng khi lưu.
 */
export function quyenNgoaiDanhMuc(
  quyen: readonly string[],
  danhMuc: readonly Permission[],
): string[] {
  const co = new Set(danhMuc.map((q) => q.code));
  return quyen.filter((q) => !co.has(q));
}

/** Hai tập có khác nhau không — để khoá nút lưu khi chưa đổi gì. */
export function khacNhau(a: Iterable<string>, b: Iterable<string>): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return true;
  for (const x of sa) if (!sb.has(x)) return true;
  return false;
}

/**
 * Tập gửi lên theo thứ tự cố định (theo danh mục), để `pointer`
 * `/permissions/{i}` của lỗi `422` tra ngược đúng mã quyền.
 */
export function sapTheoDanhMuc(daChon: ReadonlySet<string>, danhMuc: readonly Permission[]): string[] {
  return danhMuc.map((q) => q.code).filter((c) => daChon.has(c));
}

/** Một lỗi theo trường trong `errors[]` của Problem Details. */
export interface LoiConTro {
  pointer?: string;
  detail: string;
}

/**
 * Lỗi theo đường dẫn, khoá đã bỏ tiền tố `#`: `{ '/code': '…' }`. Khác
 * `loiTheoTruong` của `lib/errors` ở chỗ **không** đòi mã `INVALID_ARGUMENT`:
 * hợp đồng vai trò trả `pointer` cả với `409` (mã trùng) và `422` (quyền không
 * gán được).
 */
export function loiTheoConTro(chiTiet: unknown): Record<string, string> {
  if (!Array.isArray(chiTiet)) return {};
  const ra: Record<string, string> = {};
  for (const l of chiTiet as LoiConTro[]) {
    if (l && typeof l.pointer === 'string') ra[l.pointer.replace(/^#/, '')] = l.detail;
  }
  return ra;
}

/**
 * Tra `pointer` `/permissions/{i}` về mã quyền đã gửi ở vị trí `i`:
 * `{ 'ocr:document:delete': 'Quyền này …' }`. Chỉ số ngoài mảng thì bỏ qua —
 * lỗi đó không trỏ được vào ô nào, chỗ gọi tự hiện ở dải chung.
 */
export function loiTheoQuyen(
  loiTruong: Record<string, string>,
  daGui: readonly string[],
): Record<string, string> {
  const ra: Record<string, string> = {};
  for (const [duongDan, thongDiep] of Object.entries(loiTruong)) {
    const m = /^\/permissions\/(\d+)$/.exec(duongDan);
    const ma = m ? daGui[Number(m[1])] : undefined;
    if (ma) ra[ma] = thongDiep;
  }
  return ra;
}

/** Tìm vai trò theo mã hoặc tên, không phân biệt hoa thường và dấu. */
export function locVaiTro(ds: readonly Role[], tuKhoa: string): Role[] {
  const bo = (s: string) =>
    s
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .toLowerCase();
  const k = bo(tuKhoa.trim());
  if (!k) return [...ds];
  return ds.filter((r) => bo(r.code).includes(k) || bo(r.name).includes(k));
}

/** Module (mã) mà vai trò có quyền, theo thứ tự xuất hiện, không lặp. */
export function moduleCuaVaiTro(quyen: readonly string[]): string[] {
  return [...new Set(quyen.map(moduleCuaQuyen))];
}
